import styled, { css } from 'styled-components/macro';
import { hideVisually } from 'polished';
import { LinkWithSearchParams as Link } from 'components/base/Link/Link';
import CloseDropdownHandler from 'components/utility/CloseDropdownHandler/CloseDropdownHandler';
import modularScale from 'styles/helpers/modular-scale';

import { ReactComponent as WMGLogo } from './wmg-logo.svg';
import { topBarButton } from 'styles/base-classes';
import { ThemeProps } from 'types/styled';

export const StyledLink = styled('a')`
  display: flex;
  align-items: center;
  height: 3em;
`;

export const StyledWMGLogo = styled(WMGLogo)<any>`
  width: 34px;
  height: 32px;
`;

export const LogoWrapper = styled('div')<{ isMobileScreen?: boolean }>`
  margin: ${({ isMobileScreen }): string =>
    isMobileScreen ? '4px' : '30px 16px'};
`;

const stickyTopBar = css`
  position: fixed;
  left: 0;
  top: 0;
`;

export const TopBarWrapper = styled.div<ThemeProps<'div'>>`
  position: relative;
  width: 100%;
  height: ${({ theme, isStatic, isMobileScreen, isHomePage }): string =>
    isStatic
      ? isMobileScreen
        ? '75px'
        : !isHomePage
        ? '70px'
        : '100px'
      : theme.topbarHeight};
  z-index: 2;

  ${({ isSticky }) => isSticky && stickyTopBar};
  pointer-events: ${({ isVisible }) => (isVisible ? 'all' : 'none')};
`;

export const StyledTopBar = styled('nav')<{
  gradientColor?: 'blue';
  isVisible?: boolean;
  isStatic?: boolean;
  isMobileScreen?: boolean;
  isHomePage?: boolean;
  isAuthenticated?: boolean;
}>`
  height: inherit;
  display: flex;
  justify-content: space-between;
  color: ${({ theme }) => theme.NightBlue};
  transform: ${({ isVisible }) =>
    isVisible ? 'translateY(0)' : 'translateY(-100%)'};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};

  position: relative;
  z-index: 1;
  transition: 0.3s ease all;

  g {
    color: #fff;
    fill: #fff;
  }
  line {
    stroke: #fff !important;
  }
  path {
    fill: #fff;
  }

  ${({ gradientColor, isAuthenticated }) =>
    !isAuthenticated
      ? css`
          background: linear-gradient(
            90deg,
            ${({ theme }) => theme.GradientBlue},
            ${({ theme }) => theme.GradientGreen}
          );
        `
      : !gradientColor
      ? css`
          background: linear-gradient(
            90deg,
            rgb(32, 168, 204),
            rgb(31, 192, 131)
          );
        `
      : gradientColor &&
        gradientColor === 'blue' &&
        css`
          background: linear-gradient(
            90deg,
            ${({ theme }) => theme.GradientTopicDarkBlue},
            ${({ theme }) => theme.GradientTopicLightBlue}
          );
        `}

  ${({ isStatic, isMobileScreen, isHomePage }): string =>
    isStatic
      ? `
    opacity: 1;
    transform: none;
    background: none;
    height: ${isMobileScreen ? 75 : !isHomePage ? 70 : 100}px;
  `
      : ''}
`;

export const TopBarTitle = styled('div')`
  flex: 0 1 100%;
  overflow: hidden;

  h2 {
    ${hideVisually()};
  }
`;

export const DefaultTopBar = styled.div<ThemeProps<'div'>>`
  height: ${(props) => props.theme.topbarHeight};
  transform: ${(props) =>
    props.isVisible ? 'none' : 'translate3d(0,-100%,0)'};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: 0.5s ease all;
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 10;
  ${({ isStatic, isMobileScreen, isHomePage }): string =>
    isStatic
      ? `height: ${isMobileScreen ? 75 : !isHomePage ? 70 : 100}px;`
      : ''}
`;

export const TopicBarWrapper = styled.div<
  ThemeProps<'div'> & {
    isSmall: boolean;
    inNewDesign?: boolean;
    gradientColor?: 'blue';
    isSmallScreen?: boolean;
    fixedHeight?: boolean;
    isTopLists?: boolean;
  }
>`
  padding: ${({ inNewDesign, isSmallScreen }): string =>
    inNewDesign && isSmallScreen ? '0' : '10px 16px'};
  min-height: ${({ isSmall, inNewDesign }): string =>
    isSmall ? '3.25em' : inNewDesign ? '60px' : '71px'};
  max-height: ${({ isSmallScreen, fixedHeight }) =>
    isSmallScreen && !fixedHeight ? 'auto' : '71px'};
  ${({ fixedHeight, isSmallScreen }) =>
    fixedHeight &&
    `
  min-height: ${isSmallScreen ? '60' : '50'}px;
  height: ${isSmallScreen ? '60' : '50'}px;
  `}
  display: flex;
  justify-content: start;
  align-items: ${({ fixedHeight, isSmallScreen }) =>
    fixedHeight && isSmallScreen ? 'start' : 'center'};
  top: -17.25em;
  transition: 0.5s ease all;
  position: relative;
  flex-direction: ${({ isSmallScreen }): string =>
    isSmallScreen ? 'column' : 'row'};

  ${({ isTopLists }) =>
    isTopLists &&
    `
    padding: 15px 0 !important;
    min-height: 50px;
    height: 60px;
  `}

  ${({ gradientColor }) =>
    !gradientColor
      ? css`
          background: linear-gradient(90deg, #1c96bb 1.32%, #1baa7b 98.68%);
        `
      : gradientColor &&
        gradientColor === 'blue' &&
        css`
          background: linear-gradient(
            90deg,
            ${({ theme }) => theme.GradientDarkBlue},
            ${({ theme }) => theme.GradientLightBlue}
          );
        `}

  &.visible {
    top: ${({ isSmallScreen, fixedHeight, isTopLists }) =>
      isSmallScreen && !fixedHeight && !isTopLists
        ? '-3em'
        : isSmallScreen
        ? '-1px'
        : '0'};
    & div {
      opacity: 1;
    }
  }
  &.top-lists {
    overflow-x: auto;
  }

  & div {
    opacity: 0;
    transition: opacity 0.5s;
  }

  @media screen and (max-width: 40em) {
    ${({ inNewDesign }) =>
      inNewDesign
        ? css`
            padding: 0;
          `
        : ''};
  }
`;

export const TopbarLink = styled(Link)<any>`
  ${topBarButton};
  font-weight: 600;
  ${({ roundHover, isActive }) =>
    roundHover &&
    `
    ${isActive ? 'background: rgba(0, 0, 0, 0.1);' : ''}
    min-width: 40px;
    height: 40px;
    width: 40px;
    border-radius: 50%;
  `}
  ${({ mobileRow }) =>
    mobileRow &&
    `
    width: 100%;
    justify-content: flex-start;
  `}

  svg {
    margin-right: ${({ isAuthenticated }): any =>
      !isAuthenticated ? '0.8em' : 0};
    ${({ withoutScale }) =>
      !withoutScale &&
      css`
        transform: scale(0.8);
      `}
  }

  @media only screen and (max-width: 25em) {
    min-width: 2em;
  }
`;

export const SearchIconWrapper = styled('div')`
  height: 25px;

  svg {
    margin-right: 0;
  }
`;

export const TobbarSearchLink = styled('div')`
  a {
    padding: 0 0.3125em;
  }

  svg {
    width: 29px;
    height: 26px;
  }

  g {
    fill: none;
  }
  path:first-of-type {
    fill: none;
  }
`;

export const HomeLink = TopbarLink.withComponent(Link);

export const HomeLinkLabel = styled('span')``;

export const TopBarButtons = styled.div<ThemeProps<'div'>>`
  position: absolute;
  z-index: 10;
  right: 0;
  top: 0;
  display: flex;
  height: 100%;
  transform: ${(props) => (props.isVisible ? 'none' : 'translateY(-100%)')};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: 0.5s ease all;
  flex-shrink: 0;
  ${({ isStatic }): string =>
    isStatic ? 'align-items: center; padding-right: 32px;' : ''}
  ${({ isStatic, isMobileScreen }): string =>
    isStatic && isMobileScreen ? 'padding-right: 10px;' : ''}

  /* NOTE: min-width should equal components/notifications/Events/index => <Absolute /> */
  @media only screen and (min-width: 50em) {
    transform: none;
    opacity: 1;
    position: static;
    right: auto;
    top: auto;
    transition: none;
  }
`;

export const DropdownButton = styled.button<ThemeProps<'div'>>`
  ${topBarButton};
  margin-left: 0;
  margin-right: 0;
  ${({ withoutPadding }) => withoutPadding && 'padding: 0;'}
  ${({ isOpen }) => isOpen && 'background: rgba(0, 0, 0, 0.1);'};

  svg {
    transform: scale(0.8);
  }

  path {
    stroke: none;
  }

  @media only screen and (max-width: 25em) {
    min-width: 2em;
  }

  svg {
    margin: 0;
  }

  abbr {
    text-decoration: none;
    margin-left: ${({ theme }) => theme.spacing.xsmall};
    font-size: ${modularScale(-1)};
  }
  @media screen and (min-width: 44rem) {
    padding: 0;
  }

  @media screen and (min-width: 48em) {
    abbr {
      display: none;
    }
  }

  @media screen and (max-width: 43.9375em) {
    min-width: 40px;
    height: 40px;
    border-radius: 50%;
  }
`;

export const ChartsIconWrap = styled('div')<{
  isStatic?: boolean;
  width?: string;
}>`
  a {
    padding: 0 0.3125em;
    box-shadow: none;
  }
  svg {
    margin-right: 0;
  }
  path {
    fill: #fff !important;
    stroke: none;
  }
  ${({ width }) => width && `width: ${width};`}
`;

export const PlaylistActivityIconWrapper = styled('div')<any>`
  a {
    padding: 0 0.3125em;
    box-shadow: none;
  }
  svg {
    margin-right: 0;
    transform: none;
  }
  path {
    fill: #fff !important;
    stroke: none;
  }
  ${({ width }) => width && `width: ${width};`}
`;

export const OfficialChartsIconWrapper = styled(ChartsIconWrap)<{
  isStatic?: boolean;
  width?: string;
}>`
  svg {
    width: 27px;
    height: 21px;
  }
  path,
  line {
    stroke: #fff;
  }
  ${({ width }) => width && `width: ${width};`}
`;

export const TopListsIconWrapper = styled(ChartsIconWrap)<any>`
  margin-right: ${({ theme }) => theme.spacing.xxsmall};
  ${({ width }) => width && `width: ${width};`}

  path,
  line {
    fill: #fff;
  }
`;

export const MobileMenuIconWrapper = styled('div')<{ isActive?: boolean }>`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ isActive }): string =>
    isActive ? 'background: rgba(5, 102, 112, 0.2);' : ''}
`;

export const FilterTitleWrapper = styled('div')<any>`
  position: relative;
  left: calc(50% - 34px);
  top: ${({ topicFiltersInView }) => (topicFiltersInView ? '-100%' : '0')};
  transform: translateX(-50%);
  transition: top 0.5s;
  @media screen and (min-width: 400px) {
    left: calc(50% - 48px);
  }
  @media screen and (min-width: 700px) {
    left: calc(50% - 66px);
  }
`;

export const MobileMenuWrapper = CloseDropdownHandler(styled('div')<any>`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 100%;
  width: 100vw;
  right: 0;
  align-items: baseline;
  background: linear-gradient(90deg, rgb(32, 168, 204), rgb(31, 192, 131));
`);

export const MobileMenuText = styled('p')<any>`
  font-size: 18px;
  color: #fff;
  font-weight: 400;
  ${({ margin }) => margin && `margin: ${margin};`}
`;

export const MobIconWrapper = styled('div')<any>`
  margin-left: 35px;
  width: 55px;
  height: 26px;
  ${({ height }): string | undefined => height && `height: ${height};`}
  ${({ width }): string | undefined => width && `svg {width: ${width};}`}
`;

export const CompareDropdownWrapper = styled('div')<any>`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  padding-right: 50px;
  ${({ isOpened }): string | undefined => isOpened && 'margin-bottom: 10px;'}
`;

export const CaretIconWrapper = styled('div')`
  svg {
    path {
      fill: none;
    }
  }
`;

export const CompareWrapper = styled('div')`
  display: flex;
  align-items: center;
  height: 48px;
`;

export const WMGLogoWrapper = styled('div')<any>`
  height: 32px;
  path {
    fill: ${({ isAuthenticated, theme }: any): string =>
      isAuthenticated ? '#fff' : theme.NightBlue} !important;
  }
`;

export const InfoIconWrapper = styled('div')<any>`
  g {
    color: #000;
    fill: none;
  }
  circle {
    stroke: #fff;
  }
  a {
    padding: 0 0.3125em;
    box-shadow: none;
  }
  svg {
    margin-right: 0;
    width: 28px;
    height: 28px;
  }
  path {
    fill: #fff !important;
    stroke: #fff;
  }
`;
